import type { BuildingType, BuildingView } from './colony-api';
import {
  drawTerrainBackground,
  gridToProjected,
  projectedToGridCell,
  rowWidthRatio,
  type ThemeColors,
} from '../worldmap/worldmap-renderer';
import { wallArtForLevel, type WallArtTier } from '../../engine/wall-art';
import { buildingArtForLevel, buildingSiteArtFor } from '../../engine/building-art';

/**
 * Pure canvas math and drawing helpers for the colony "city view" — the
 * player's buildings on a fixed 5x5 plot grid, drawn with the same tilted
 * trapezoid projection as the world map (see worldmap-renderer.ts) so both
 * screens read as the same world. Kept separate from DOM/event wiring
 * (colony-city-view.ts) so the plot<->pixel math and hit-testing can be
 * unit-tested without a real canvas context.
 *
 * The projection helpers are imported from worldmap-renderer rather than
 * reimplemented — they take gridWidth/gridHeight/farWidthRatio as
 * parameters, so a 5x5 plot grid is just a different call, not a different
 * projection.
 */

/** The city view is always a fixed 5x5 conceptual plot grid, unlike the 80x40 world map. */
export const PLOT_GRID_SIZE = 5;

/**
 * How wide the far (top) edge is relative to the near (bottom) edge. Less
 * aggressive than the world map's convergence — with only 5 columns, a hard
 * taper makes the back row's plots too narrow to read.
 */
export const CITY_FAR_EDGE_WIDTH_RATIO = 0.68;

/**
 * Caps the tilted plot's on-screen height as a fraction of its width. A 5x5
 * grid stretched to fill a wide desktop section would otherwise look
 * extremely squashed; this keeps the tilt's proportions stable and letterboxes
 * (vertically centers) whatever height is left over. Lower = a steeper,
 * more top-down tilt.
 */
export const CITY_HEIGHT_WIDTH_RATIO = 0.5;

/** Fallback size used only when the host container has no measurable size yet. */
const DEFAULT_CITY_SIZE = 5 * 48;

/** Shrinks the plot ~10% inside its container so the tilted grid doesn't crowd the viewport edges. */
export const CITY_GRID_SCALE = 0.9;

/** Mirrors backend MAX_BUILDING_LEVEL — used only as the safe fallback when a buildings list arrives without a Command Center. */
const MAX_BUILDING_LEVEL_MIRROR = 10;

/**
 * Mirrors backend BUILDING_TECH_GATE — the researched tech level a type
 * needs. REGOLITH_MINE, WATER_ICE_CONDENSER, BARRACKS, BASTION_WALL and
 * BUILDER_CAMP sit at 0 (TODO.md item 5's HQ_1 starter set — see the
 * backend constant's own doc comment for why these aren't 1 the way
 * HE3_REACTOR/COMPOSITE_FABRICATOR/STRATEGIC_RESERVE still are). Only
 * SCOUT_GARAGE (2) and SIEGE_WORKSHOP (8) sit above 1.
 */
export const BUILDING_TECH_GATE_MIRROR: Record<BuildingType, number> = {
  COMMAND: 0,
  REGOLITH_MINE: 0,
  WATER_ICE_CONDENSER: 0,
  HE3_REACTOR: 1,
  COMPOSITE_FABRICATOR: 0,
  BARRACKS: 0,
  SCOUT_GARAGE: 2,
  BASTION_WALL: 0,
  SIEGE_WORKSHOP: 8,
  BUILDER_CAMP: 0,
  STRATEGIC_RESERVE: 1,
};

/**
 * Mirrors backend BUILDING_HQ_UNLOCK — the Command Center level a type needs
 * before it can be built at all. A SECOND gate alongside the tech gate
 * above; both must pass. Display-only, like every mirrored constant here —
 * the server enforces it regardless of what this says.
 *
 * Lives in this module rather than colony-screen.ts because BOTH the screen
 * (for the modal's build affordance) and this renderer (for whether a plot
 * shows a construction site at all) need it, and colony-screen.ts already
 * imports from here — the reverse direction would be a cycle.
 */
export const BUILDING_HQ_UNLOCK: Record<BuildingType, number> = {
  COMMAND: 0,
  BUILDER_CAMP: 1,
  REGOLITH_MINE: 1,
  WATER_ICE_CONDENSER: 1,
  BASTION_WALL: 1,
  BARRACKS: 1,
  SCOUT_GARAGE: 2,
  STRATEGIC_RESERVE: 3,
  SIEGE_WORKSHOP: 4,
  COMPOSITE_FABRICATOR: 3,
  HE3_REACTOR: 6,
};

/**
 * Fixed plot layout, per the game design: Command Center sits at the center
 * like a town hall, with the other buildings arranged around it.
 */
export const BUILDING_PLOTS: Record<
  BuildingType,
  {
    col: number;
    row: number;
    /**
     * Fine art-direction nudge to the DRAWN center, in fractional cell
     * units — applied only in plotCenterCanvas (the visual sprite position),
     * never to hit-testing (BUILDING_AT_PLOT still keys off the base
     * col/row, keeping the whole cell as the tap target). Omit for 0.
     */
    xOffset?: number;
  }
> = {
  COMMAND: { col: 2, row: 2 },
  // One step diagonally in from the bottom-left corner (the plot grid's
  // (0, 4) cell) -- this is only the tap-target/marker position for opening
  // the wall's build modal; the decorative ring art itself wraps the whole
  // grid's perimeter via resolveWallRingDraw, independent of this entry.
  BASTION_WALL: { col: 1, row: 3 },
  REGOLITH_MINE: { col: 0, row: 0 },
  WATER_ICE_CONDENSER: { col: 4, row: 0 },
  // Nudged toward the right border of its own cell, per art-direction
  // feedback — col 1 is otherwise empty on this row, so this doesn't
  // collide with another plot's art.
  BUILDER_CAMP: { col: 0, row: 2, xOffset: 0.4 },
  BARRACKS: { col: 1, row: 1 },
  SCOUT_GARAGE: { col: 3, row: 2, xOffset: 0.3 },
  HE3_REACTOR: { col: 0, row: 4 },
  COMPOSITE_FABRICATOR: { col: 3, row: 1 },
  // Front-right corner. It sat at (2, 4) -- dead centre of the front row,
  // directly below COMMAND, which read as a second focal point competing
  // with the HQ.
  SIEGE_WORKSHOP: { col: 4, row: 4 },
  STRATEGIC_RESERVE: { col: 4, row: 2 },
};

function plotKey(col: number, row: number): string {
  return `${col},${row}`;
}

/** Inverse of BUILDING_PLOTS — lets hit-testing go straight from a resolved grid cell to its building. */
const BUILDING_AT_PLOT: Map<string, BuildingType> = new Map(
  (Object.keys(BUILDING_PLOTS) as BuildingType[]).map((type) => [
    plotKey(BUILDING_PLOTS[type].col, BUILDING_PLOTS[type].row),
    type,
  ]),
);

export interface CityGeometry {
  /** Width of the trapezoid's near (bottom, widest) edge, in canvas px. */
  displayWidth: number;
  /** Total height of the tilted plot, in canvas px. */
  displayHeight: number;
  /** Canvas-space pan applied to the projection — centers the plot in a container of arbitrary aspect ratio. */
  offsetX: number;
  offsetY: number;
}

/**
 * Contain-fits a FIXED-aspect-ratio content box (width:height = 1:heightWidthRatio)
 * inside the container, then shrinks it by CITY_GRID_SCALE and centers the
 * result on both axes. The whole scene (background photo AND the plot grid —
 * see drawCityScene) shares this one box, so resizing the container can never
 * make buildings drift relative to the backdrop: it only changes how much
 * letterbox/pillarbox margin surrounds the same fixed-ratio picture, never
 * the picture's own proportions.
 *
 * This must be a genuine contain-fit (shrink whichever dimension is slack,
 * not just cap one axis) — an earlier version computed
 * `displayWidth = safeWidth * SCALE` unconditionally and only capped height,
 * which kept the box's width pinned to the full container even when the
 * container was shorter than the target ratio, silently distorting the
 * aspect ratio instead of letterboxing it.
 */
export function computeCityGeometry(
  containerWidth: number,
  containerHeight: number,
  heightWidthRatio: number = CITY_HEIGHT_WIDTH_RATIO,
): CityGeometry {
  const safeWidth = containerWidth > 0 ? containerWidth : DEFAULT_CITY_SIZE;
  const safeHeight = containerHeight > 0 ? containerHeight : DEFAULT_CITY_SIZE;
  const widthIfHeightBound = safeHeight / heightWidthRatio;
  const contentWidth = Math.min(safeWidth, widthIfHeightBound);
  const contentHeight = contentWidth * heightWidthRatio;
  const displayWidth = contentWidth * CITY_GRID_SCALE;
  const displayHeight = contentHeight * CITY_GRID_SCALE;
  return {
    displayWidth,
    displayHeight,
    offsetX: (safeWidth - displayWidth) / 2,
    offsetY: (safeHeight - displayHeight) / 2,
  };
}

/**
 * Unit-space projected point -> canvas pixel. Mirrors the `toScreen` helper
 * inside worldmap-renderer's drawGridLines exactly (including its
 * `+ displayWidth / 2` horizontal centering) — the grid mesh and everything
 * drawn on top of it must share one convention or markers sit off their cells.
 */
function projectedToCanvas(xu: number, yu: number, geometry: CityGeometry): { x: number; y: number } {
  return {
    x: xu * geometry.displayWidth + geometry.offsetX + geometry.displayWidth / 2,
    y: yu * geometry.displayHeight + geometry.offsetY,
  };
}

/** Grid coordinate (edges are integers, so a cell's own center is col+0.5, row+0.5) -> canvas pixel. */
export function gridToCanvas(gx: number, gy: number, geometry: CityGeometry): { x: number; y: number } {
  const p = gridToProjected(gx, gy, PLOT_GRID_SIZE, PLOT_GRID_SIZE, CITY_FAR_EDGE_WIDTH_RATIO);
  return projectedToCanvas(p.x, p.y, geometry);
}

/** Canvas-space center of a building's plot. */
export function plotCenterCanvas(type: BuildingType, geometry: CityGeometry): { x: number; y: number } {
  const plot = BUILDING_PLOTS[type];
  return gridToCanvas(plot.col + 0.5 + (plot.xOffset ?? 0), plot.row + 0.5, geometry);
}

/**
 * On-screen size of one plot cell. Width is row-dependent under the
 * trapezoid (far rows are narrower); height is uniform.
 */
export function plotCellSize(row: number, geometry: CityGeometry): { width: number; height: number } {
  const t = (row + 0.5) / PLOT_GRID_SIZE;
  return {
    width: (geometry.displayWidth * rowWidthRatio(t, CITY_FAR_EDGE_WIDTH_RATIO)) / PLOT_GRID_SIZE,
    height: geometry.displayHeight / PLOT_GRID_SIZE,
  };
}

/**
 * Resolve a canvas-space click to the building occupying that plot, or null.
 * Inverts the projection rather than testing a radius around each marker, so
 * the entire cell is the tap target (which is what keeps plots comfortably
 * above the 44px minimum) and so a tilted cell's real trapezoid shape is
 * respected instead of an approximating circle.
 */
export function hitTestBuilding(canvasX: number, canvasY: number, geometry: CityGeometry): BuildingType | null {
  if (geometry.displayWidth <= 0 || geometry.displayHeight <= 0) return null;
  const xu = (canvasX - geometry.offsetX - geometry.displayWidth / 2) / geometry.displayWidth;
  const yu = (canvasY - geometry.offsetY) / geometry.displayHeight;
  const cell = projectedToGridCell(xu, yu, PLOT_GRID_SIZE, PLOT_GRID_SIZE, CITY_FAR_EDGE_WIDTH_RATIO);
  if (!cell) return null;
  return BUILDING_AT_PLOT.get(plotKey(cell.x, cell.y)) ?? null;
}

export interface BuildingVisual {
  /** true once level >= 1 — drives whether the art/icon is drawn at full or faded (not-yet-built) opacity. */
  built: boolean;
  constructing: boolean;
  /** Bright ring drawn around a building currently under construction (including a first-ever construction at level 0). */
  ringColor?: string;
  /** Golden glow hugging the art/icon itself (via shadowBlur) when the pointer is hovering this building — independent of ringColor, so both can show at once. */
  hoverGlowColor?: string;
}

/**
 * Pure mapping from a building's server state to the colors it should be
 * drawn with. `hovered` drives hoverGlowColor.
 *
 * Deliberately does NOT scale by level — an earlier version grew the plot
 * radius from level 1 to 9 via a formula independent of which art tier was
 * showing, which meant the SAME tier image (e.g. a ladder step covering
 * levels 3-5) rendered at a different size depending on the exact level
 * within that range. Any size growth across levels now comes only from the
 * art itself — each tier's own canvas occupancy (see building-art.ts) — not
 * from a level-driven formula here.
 */
export function resolveBuildingVisual(building: BuildingView, colors: ThemeColors, hovered = false): BuildingVisual {
  return {
    built: building.level >= 1,
    constructing: building.constructing,
    ringColor: building.constructing ? colors.goldBright : undefined,
    hoverGlowColor: hovered ? colors.goldBright : undefined,
  };
}

export interface BuildingArtDraw {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Building-art sprites are drawn at BUILDING_ART_BASE_RATIO times this scale — large enough to read as an actual building, not an icon, even though that means neighboring plots' sprites visually overlap at the trapezoid's near rows. */
const BUILDING_ART_BASE_RATIO = 2.6;
const BUILDING_ART_SCALE = 1.95;

/** Per-building-type scale overrides, relative to the same BUILDING_ART_BASE_RATIO — some renders read better bigger/smaller than the default (e.g. Builder Camp and Command's wider silhouettes vs. Regolith Mine's). Falls back to BUILDING_ART_SCALE for every type not listed. */
const BUILDING_ART_SCALE_OVERRIDES: Partial<Record<BuildingType, number>> = {
  BUILDER_CAMP: 2.05,
  // 10% larger than the other wide-silhouette buildings — the Command
  // Center is the colony's core building and now gates both the
  // incoming-attack warning window and the max-colonies cap, so it should
  // read as visually dominant on the plot, not same-sized as Builder Camp.
  COMMAND: 2.05 * 1.1,
  REGOLITH_MINE: 1.8,
  // Scaled down slightly, per art-direction feedback.
  WATER_ICE_CONDENSER: 1.75,
  // Scaled down twice now, per repeated art-direction feedback — it read too
  // large across every level, not just one tier, and still did after the
  // first pass. A TYPE-level entry (rather than a per-tier scaleMultiplier)
  // is deliberate: it is the only thing the level-0 construction site also
  // picks up, and the site was called out as oversized along with the
  // finished building.
  SCOUT_GARAGE: 1.56,
  STRATEGIC_RESERVE: 1.25,
};

export function buildingArtScale(type: BuildingType): number {
  return BUILDING_ART_SCALE_OVERRIDES[type] ?? BUILDING_ART_SCALE;
}

/** Whether a building plot should accept interaction (click, hover). A plot that draws nothing (locked prerequisites AND not yet built) is invisible and should not respond to input. */
export function isBuildingPlotInteractive(type: BuildingType, level: number, hqLevel: number, techLevel: number): boolean {
  if (level >= 1) return true;
  return hqLevel >= BUILDING_HQ_UNLOCK[type] && techLevel >= BUILDING_TECH_GATE_MIRROR[type];
}

/**
 * How far below the cell's own vertical center the image's bottom edge is
 * anchored, as a multiple of `radius` — 1.0 would land exactly on radius
 * itself, which is well short of the cell's true bottom edge (that's at
 * 0.5/0.38 ≈ 1.32x radius, since radius is only 0.38x the cell's shorter
 * dimension). The default keeps every building comfortably inside its own
 * cell rather than touching the grid line. Per-type overrides exist for a
 * building whose art was specifically called out as needing its base to
 * land ON the grid line instead of floating inside the cell — reducing the
 * source image's own internal padding (see building-art.ts/asset-list.md)
 * gets a tier's CONTENT closer to the image's own bottom edge, but can never
 * move that edge past this ratio; only this constant can.
 *
 * A type-level override here applies to EVERY tier of that type — Command
 * Center was tried this way first and it wrongly moved its L3/L6 art too,
 * when only L1 had actually been called out. A tier called out individually
 * gets `BuildingArtTier.groundRatio` instead (building-art.ts), which
 * replaces this type-level value for that one tier only — see Command
 * Center's L1 entry for the worked example.
 */
const BUILDING_ART_GROUND_RATIO = 0.9;

/** Per-building-type ground-anchor overrides — see BUILDING_ART_GROUND_RATIO. Falls back to it for every type not listed. Empty today; kept for the next building called out as a WHOLE TYPE needing a different anchor, as opposed to one specific tier. */
const BUILDING_ART_GROUND_RATIO_OVERRIDES: Partial<Record<BuildingType, number>> = {};

export function buildingArtGroundRatio(type: BuildingType): number {
  return BUILDING_ART_GROUND_RATIO_OVERRIDES[type] ?? BUILDING_ART_GROUND_RATIO;
}

/**
 * Where and how large to draw a loaded building-art image, given the same
 * `radius` drawBuildingPlot would have used for its circle fallback —
 * fit to a bounding box a bit larger than that circle (photographic building
 * renders read as too small if constrained to the marker's own footprint),
 * aspect-preserved, and anchored so its visual base sits near the bottom of
 * that box rather than centered — buildings stand on the ground, they don't
 * float around the cell's vertical center the way a single glyph can.
 */
export function resolveBuildingArtDraw(
  center: { x: number; y: number },
  radius: number,
  naturalWidth: number,
  naturalHeight: number,
  scale: number = BUILDING_ART_SCALE,
  groundRatio: number = BUILDING_ART_GROUND_RATIO,
): BuildingArtDraw | null {
  if (radius <= 0 || naturalWidth <= 0 || naturalHeight <= 0) return null;
  const boxSize = radius * BUILDING_ART_BASE_RATIO * scale;
  const aspect = naturalHeight / naturalWidth;
  const width = boxSize;
  const height = boxSize * aspect;
  return {
    x: center.x - width / 2,
    y: center.y + radius * groundRatio - height,
    width,
    height,
  };
}

/** Draws one building marker at a canvas-space center. `cellSize` is the plot's own on-screen size, which varies by row under the tilt. `artImage`, if given (a loaded, cache-hit image for this building's current level tier — see building-art.ts), replaces the plain circle fallback entirely. `artScale` overrides BUILDING_ART_SCALE for this specific building type — see buildingArtScale. `groundRatio` overrides BUILDING_ART_GROUND_RATIO — see buildingArtGroundRatio. */
/**
 * Canvas-space point halfway up a building's drawn art — its visual middle — or the plot centre
 * when the art is not loaded. The plot centre is the footprint on the ground, which for a tall
 * building (the Command Center) sits well below the building's body, so anything meant to read as
 * "on the building" (its countdown badge) is anchored here instead. Mirrors drawOneBuilding's
 * radius/scale/ground maths so the two cannot drift apart.
 */
export function buildingArtMiddle(
  type: BuildingType,
  level: number,
  geometry: CityGeometry,
  artImageFor?: (src: string) => HTMLImageElement | undefined,
): { x: number; y: number } {
  const center = plotCenterCanvas(type, geometry);
  const tier = buildingArtForLevel(type, level);
  const img = tier ? artImageFor?.(tier.src) : undefined;
  if (!tier || !img || img.naturalWidth <= 0) return center;
  const cell = plotCellSize(BUILDING_PLOTS[type].row, geometry);
  const radius = Math.min(cell.width, cell.height) * 0.38;
  const plan = resolveBuildingArtDraw(
    center,
    radius,
    img.naturalWidth,
    img.naturalHeight,
    buildingArtScale(type) * (tier.scaleMultiplier ?? 1),
    tier.groundRatio ?? buildingArtGroundRatio(type),
  );
  return plan ? { x: center.x, y: plan.y + plan.height * (tier.contentMidY ?? 0.5) } : center;
}

/**
 * The Command Center is tall, so its footprint ring (drawn at the plot centre) sat below the building's base. Its ring
 * is lifted by this many radii so the building stands inside it. Other buildings' rings are untouched.
 */
export const COMMAND_RING_LIFT = 0.35;

export function drawBuildingPlot(
  ctx: CanvasRenderingContext2D,
  visual: BuildingVisual,
  center: { x: number; y: number },
  cellSize: { width: number; height: number },
  artImage?: HTMLImageElement,
  artScale?: number,
  groundRatio?: number,
  /** Raises the construction ring by this many radii (see COMMAND_RING_LIFT). */
  ringLift = 0,
): void {
  const radius = Math.min(cellSize.width, cellSize.height) * 0.38;

  ctx.save();
  ctx.globalAlpha = 1;

  if (visual.hoverGlowColor) {
    ctx.shadowColor = visual.hoverGlowColor;
    ctx.shadowBlur = 16;
  }

  if (artImage && artImage.naturalWidth > 0) {
    const plan = resolveBuildingArtDraw(center, radius, artImage.naturalWidth, artImage.naturalHeight, artScale, groundRatio);
    if (plan) ctx.drawImage(artImage, plan.x, plan.y, plan.width, plan.height);
  }

  // Reset the glow before the construction ring so it never bleeds onto that
  // separate indicator — the two are visually independent signals.
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;

  if (visual.ringColor) {
    ctx.strokeStyle = visual.ringColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(center.x, center.y - radius * ringLift, radius + 4, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
}

/** Noise resolution for the terrain fill — decoupled from the 5x5 plot grid so the backdrop doesn't read as an obvious checkerboard. */
const TERRAIN_NOISE_X = 16;
const TERRAIN_NOISE_Y = 10;

export interface CoverFitDraw {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * CSS `object-fit: cover`-equivalent placement for drawImage: scales the
 * source image up just enough that it fully covers the canvas on both axes
 * (the larger of the two axis ratios wins), then centers the overflow crop.
 * Returns null for a not-yet-usable image (no natural size yet) — the caller
 * falls back to the procedural terrain in that case, same as every other
 * "art not loaded yet" branch in this file.
 */
export function resolveCoverFitDraw(canvasWidth: number, canvasHeight: number, naturalWidth: number, naturalHeight: number): CoverFitDraw | null {
  if (canvasWidth <= 0 || canvasHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) return null;
  const scale = Math.max(canvasWidth / naturalWidth, canvasHeight / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return { x: (canvasWidth - width) / 2, y: (canvasHeight - height) / 2, width, height };
}

/**
 * How much wider than the grid's own near-edge footprint (geometry.displayWidth)
 * the wall ring is drawn, so it wraps just outside the outermost plots rather
 * than sitting on top of them. Modest on purpose: geometry.displayWidth is
 * already CITY_GRID_SCALE (90%) of the container, so anything past ~1.05-1.08x
 * starts pushing into (and eventually past) the container's own edge.
 */
const WALL_RING_WIDTH_OUTSET = 1.06;

/**
 * How much taller than the grid's own height (geometry.displayHeight) the
 * ring BAND is drawn. Every source render's ring band is proportionally
 * taller relative to its width than this game's deliberately flat trapezoid
 * (CITY_HEIGHT_WIDTH_RATIO = 0.5 — a much more top-down tilt than the ring
 * art's own elevated-angle camera implies) — fitting the ring to the grid's
 * width alone would make its band noticeably taller than the grid itself,
 * spilling into the near/far rows of buildings. Scaling width and height
 * independently (below) squashes the ring to the grid's actual proportions
 * instead, at the cost of it no longer being a geometrically "true" ellipse.
 */
const WALL_RING_HEIGHT_OUTSET = 1.02;

/** Every level's wall art is correctly aligned relative to the grid — this is a small, uniform rightward nudge applied on top of that alignment, per art-direction feedback. Expressed as a fraction of geometry.displayWidth so it scales with the container like every other wall-ring measurement here. */
const WALL_RING_X_OFFSET_RATIO = 0.025;

export interface WallRingDraw {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where and how large to draw the COMBINED (rear + front halves stacked
 * back together) wall-art image so its ring band (per
 * WallArtTier.ringTop/ringBottom, both still measured against the original,
 * uncut full image) is centered on the plot grid, sized to the grid's own
 * width AND height independently (see WALL_RING_HEIGHT_OUTSET) — see
 * wall-art.ts for why tiers anchor on the ring band rather than the image's
 * own full bounds. `naturalHeight` is the SUM of both halves' own natural
 * heights, not either alone — see splitWallRingDraw, which slices this one
 * combined box back into the two halves actually drawn (rear behind
 * buildings, front in front of them). Returns null when the geometry has no
 * measurable size yet.
 */
export function resolveWallRingDraw(
  geometry: CityGeometry,
  tier: WallArtTier,
  naturalWidth: number,
  naturalHeight: number,
): WallRingDraw | null {
  if (geometry.displayWidth <= 0 || geometry.displayHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return null;
  }
  // One multiplier on both axes, so a tier can be scaled without distorting.
  const tierScale = tier.scaleMultiplier ?? 1;

  const targetWidth = geometry.displayWidth * WALL_RING_WIDTH_OUTSET * tierScale;
  const scaleX = targetWidth / naturalWidth;
  const width = naturalWidth * scaleX;

  const targetBandHeight = geometry.displayHeight * WALL_RING_HEIGHT_OUTSET * tierScale;
  const scaleY = targetBandHeight / ((tier.ringBottom - tier.ringTop) * naturalHeight);
  const height = naturalHeight * scaleY;

  const centerX = geometry.offsetX + geometry.displayWidth / 2 + geometry.displayWidth * WALL_RING_X_OFFSET_RATIO;
  const centerY = geometry.offsetY + geometry.displayHeight / 2;
  const ringBandHeight = (tier.ringBottom - tier.ringTop) * height;
  const ringBandTop = centerY - ringBandHeight / 2;

  return {
    x: centerX - width / 2,
    y: ringBandTop - tier.ringTop * height,
    width,
    height,
  };
}

/**
 * Slices resolveWallRingDraw's combined box back into the rear (top, drawn
 * behind buildings) and front (bottom, drawn in front of buildings —
 * includes the gate) halves, in the same proportion the two source images
 * actually have — not a fixed 50/50, since a future tier's halves need not
 * be cropped identically. `rearNaturalHeight`/`frontNaturalHeight` are each
 * half's own loaded-image natural height (pre-scale, pre-combination).
 */
export function splitWallRingDraw(
  plan: WallRingDraw,
  rearNaturalHeight: number,
  frontNaturalHeight: number,
): { rear: WallRingDraw; front: WallRingDraw } {
  const total = rearNaturalHeight + frontNaturalHeight;
  const rearHeight = total > 0 ? plan.height * (rearNaturalHeight / total) : plan.height / 2;
  return {
    rear: { x: plan.x, y: plan.y, width: plan.width, height: rearHeight },
    front: { x: plan.x, y: plan.y + rearHeight, width: plan.width, height: plan.height - rearHeight },
  };
}

/**
 * Full draw entry point: clears the canvas, paints the Mars-terrain backdrop
 * clipped to the tilted plot, draws the plot grid, the Bastion Wall's
 * perimeter ring art (if built and its tier's image has finished loading),
 * then every building. The ring is drawn before the buildings loop so
 * buildings render on top of it rather than being occluded; buildings are
 * drawn back-to-front (far rows first) so a near building correctly overlaps
 * the one behind it. `wallImageFor` and `buildingImageFor` are both
 * synchronous, cache-only lookups by src (see wall-art.ts's loadedWallImage
 * and building-art.ts's loadedBuildingImage) — the caller owns actually
 * loading those images, since `Image` loading is async and this function
 * must stay synchronous.
 */
export function drawCityScene(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  geometry: CityGeometry,
  buildings: BuildingView[],
  colors: ThemeColors,
  wallImageFor?: (src: string) => HTMLImageElement | undefined,
  buildingImageFor?: (src: string) => HTMLImageElement | undefined,
  backgroundImageFor?: (canvasWidthPx: number) => HTMLImageElement | undefined,
  hoveredType?: BuildingType | null,
  /**
   * The colony's researched tech level, for the construction-site gate
   * below. Defaults to Infinity so an omitted argument hides nothing — a
   * caller that does not know the tech level should not silently blank
   * plots.
   */
  techLevel: number = Infinity,
): void {
  ctx.save();
  ctx.fillStyle = colors.surface2;
  ctx.fillRect(0, 0, width, height);

  // The real background photo (colony-background.ts) cover-fits the SAME
  // fixed-aspect content box the plot grid itself sits in (derived from
  // `geometry` — CITY_GRID_SCALE already relates the two, so this is a pure
  // derivation, not a second source of truth) rather than the raw canvas —
  // that's what keeps the backdrop and the buildings scaling together as one
  // picture instead of drifting apart when the container reshapes. The
  // procedural fallback (while the photo loads, or if it never does) stays
  // full-canvas: it's a generated gradient/noise pattern with no fixed
  // landmark to misalign against, so constraining it to the content box
  // would only add letterbox bars with no benefit.
  const contentWidth = geometry.displayWidth / CITY_GRID_SCALE;
  const contentHeight = geometry.displayHeight / CITY_GRID_SCALE;
  const contentCenterX = geometry.offsetX + geometry.displayWidth / 2;
  const contentCenterY = geometry.offsetY + geometry.displayHeight / 2;
  const contentX = contentCenterX - contentWidth / 2;
  const contentY = contentCenterY - contentHeight / 2;

  drawTerrainBackground(ctx, width, height, TERRAIN_NOISE_X, TERRAIN_NOISE_Y);

  const bgImage = backgroundImageFor?.(width);
  const bgPlan = bgImage ? resolveCoverFitDraw(contentWidth, contentHeight, bgImage.naturalWidth, bgImage.naturalHeight) : null;
  if (bgImage && bgPlan) {
    ctx.drawImage(bgImage, contentX + bgPlan.x, contentY + bgPlan.y, bgPlan.width, bgPlan.height);
  }

  // No grid lines. The colony reads as a photographed site, not a board —
  // the plot lattice was scaffolding for placing art and competed with the
  // background render once every plot had its own sprite. Plot hit-testing
  // (plotCenterCanvas / plotCellSize) is unaffected: it was always computed
  // from the geometry, never read back off the drawn lines.

  // The colony's own Command Center level, which gates which plots may show a
  // construction site at all (see below).
  // Falls back to the ceiling, not 0: a buildings list without COMMAND is a
  // data anomaly, and hiding every construction site colony-wide is a far
  // worse failure than showing one the server would refuse. The server is
  // the real gate either way.
  const hqLevel = buildings.find((b) => b.type === 'COMMAND')?.level ?? MAX_BUILDING_LEVEL_MIRROR;

  function drawOneBuilding(building: BuildingView): void {
    const visual = resolveBuildingVisual(building, colors, building.type === hoveredType);
    const center = plotCenterCanvas(building.type, geometry);
    const cellSize = plotCellSize(BUILDING_PLOTS[building.type].row, geometry);
    // A built (level >= 1) building's own tier art wins; otherwise fall back
    // to its "construction site" render (level 0 only — see building-art.ts),
    // and only then to the plain circle marker.
    //
    // A site render is an INVITATION to build, so it is withheld until the
    // Command Center actually permits it — a staged work site on a plot the
    // player cannot yet use reads as a bug, not as a locked feature
    // (TODO.md #9: the construction visualisation, not just the button, is
    // gated on prerequisites). Once unlocked the site appears, which also
    // makes levelling the HQ visibly open up the colony.
    const artTier = buildingArtForLevel(building.type, building.level);
    const unlocked =
      hqLevel >= BUILDING_HQ_UNLOCK[building.type] && techLevel >= BUILDING_TECH_GATE_MIRROR[building.type];
    const siteArt = artTier || !unlocked ? null : buildingSiteArtFor(building.type, building.level);
    const artSrc = artTier?.src ?? siteArt?.src;
    const artImage = artSrc ? buildingImageFor?.(artSrc) : undefined;
    // A tier's own scaleMultiplier (an explicit, named art-direction
    // correction — see BuildingArtTier) layers on top of the per-type scale;
    // site art has no tier to carry one, so it always draws at the type's
    // plain scale.
    const artScale = buildingArtScale(building.type) * (artTier?.scaleMultiplier ?? siteArt?.scaleMultiplier ?? 1);
    // A tier's own groundRatio REPLACES (doesn't layer onto) the type-level
    // value — unlike scaleMultiplier, which multiplies in.
    const groundRatio = artTier?.groundRatio ?? buildingArtGroundRatio(building.type);
    drawBuildingPlot(ctx, visual, center, cellSize, artImage, artScale, groundRatio, building.type === 'COMMAND' ? COMMAND_RING_LIFT : 0);
  }

  // These two sit in the back row (row 0), tucked against the rear wall —
  // drawn before that wall half (unlike every other building, which draws
  // after) so the rear wall visually passes in front of them instead of them
  // sitting on top of the wall line. Composite Fabricator does NOT belong
  // here: it sits at row 1, one step inside the perimeter, so drawing it
  // before the rear wall made the wall paint over a building that should
  // read as standing inside the walls. It now falls through to the normal
  // backToFront loop below, which already draws after the rear wall half and
  // before the front wall half — exactly where a row-1 building belongs.
  const BEHIND_WALL_TYPES: BuildingType[] = ['REGOLITH_MINE', 'WATER_ICE_CONDENSER'];
  for (const type of BEHIND_WALL_TYPES) {
    const building = buildings.find((b) => b.type === type);
    if (building) drawOneBuilding(building);
  }

  // The wall ring is split into two halves so buildings sit genuinely
  // INSIDE it rather than under a single flat layer: the rear (far/top) half
  // draws now, before any building, so it sits behind everything; the front
  // (near/bottom) half — which carries the gate — draws again below, AFTER
  // every building, so it correctly occludes the buildings behind it the
  // way a near foreground wall would at this camera angle.
  const wall = buildings.find((b) => b.type === 'BASTION_WALL');
  const wallTier = wall ? wallArtForLevel(wall.level) : null;
  const wallRearImage = wallTier ? wallImageFor?.(wallTier.rearSrc) : undefined;
  const wallFrontImage = wallTier ? wallImageFor?.(wallTier.frontSrc) : undefined;
  const wallPlan =
    wallTier && wallRearImage && wallFrontImage && wallRearImage.naturalWidth > 0 && wallFrontImage.naturalWidth > 0
      ? resolveWallRingDraw(
          geometry,
          wallTier,
          wallRearImage.naturalWidth,
          wallRearImage.naturalHeight + wallFrontImage.naturalHeight,
        )
      : null;
  const wallSplit = wallPlan && wallRearImage && wallFrontImage
    ? splitWallRingDraw(wallPlan, wallRearImage.naturalHeight, wallFrontImage.naturalHeight)
    : null;
  if (wallSplit && wallRearImage) {
    ctx.drawImage(wallRearImage, wallSplit.rear.x, wallSplit.rear.y, wallSplit.rear.width, wallSplit.rear.height);
  }

  // He-3 Reactor (0, 4) and Siege Workshop (4, 4) sit at the grid's front
  // row, in its two corners, and must read as standing inside the walls, in front of
  // both the rear AND front wall halves — so, symmetrically with
  // BEHIND_WALL_TYPES above, they're excluded from the normal back-to-front
  // loop and drawn explicitly after the front wall half below.
  // Composite Fabricator is NOT here — its row-1 plot (3, 1) only needs to
  // clear the REAR wall half, which the normal backToFront loop already
  // does; it does not need to sit in front of the gate-carrying front half.
  const FRONT_OF_WALL_TYPES: BuildingType[] = ['HE3_REACTOR', 'SIEGE_WORKSHOP'];

  const backToFront = [...buildings]
    .filter((b) => !BEHIND_WALL_TYPES.includes(b.type) && !FRONT_OF_WALL_TYPES.includes(b.type))
    .sort((a, b) => BUILDING_PLOTS[a.type].row - BUILDING_PLOTS[b.type].row);
  for (const building of backToFront) {
    drawOneBuilding(building);
  }

  if (wallSplit && wallFrontImage) {
    ctx.drawImage(wallFrontImage, wallSplit.front.x, wallSplit.front.y, wallSplit.front.width, wallSplit.front.height);
  }

  for (const type of FRONT_OF_WALL_TYPES) {
    const building = buildings.find((b) => b.type === type);
    if (building) drawOneBuilding(building);
  }

  ctx.restore();
}
